/* eslint-disable @typescript-eslint/no-explicit-any */

import { FC, Key, useState, createRef } from "react";
import * as _ from "lodash";
import { useSelector } from "react-redux";
import { useDeepCompareEffect } from "use-deep-compare";

import {
  Col,
  Row,
  Analytics,
  Charts,
  DndProvider,
  HTML5Backend,
  CtraLayout,
  Button,
  Modal,
  Space,
  DeleteOutlined
} from "@ctra/components";

import { GAEvent, makeTrackEvent } from "@ctra/analytics";
import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { Enterprise, CardEntity, ISODuration, PageEntity } from "@ctra/api";
import { isMutableRef, resizeBase64Image } from "@ctra/utils";

import { CompiledRoutes } from "@routes";
import { useFarm } from "@farms";
import { useDataDictionary } from "@base";
import { GACategories, GAActions, formatFilters, useSavedCharts, makeDefaultDataFilters } from "@analytics";
import { Chart, ChartAPIContext, ChartFilterContext, FilterMetadata, SeriesDefinition } from "@chart";
import { useCurrentUser } from "@auth";

import { commonModalProps } from "../DashboardPage";
import styles from "./ChartList.module.less";

const {
  V3: { ChartCard, Empty }
} = Analytics;

const { ContentWrapper } = CtraLayout;
const { confirm } = Modal;

interface ChartListProps {
  pageID: PageEntity["id"];
}

const {
  analytics: {
    config: {
      metric: { displayName },
      variant: variantCopy,
      source: sourceCopy,
      pageTitle
    },
    v3: {
      config: { cta },
      empty,
      confirm: { removeChart: removeChartCopy }
    }
  },
  gamePlan: {
    actions: {
      delete: { confirm: confirmCopy }
    }
  }
} = Content;

/**
 * List of chart cards
 * @returns
 */
export const ChartList: FC<ChartListProps> = ({ pageID }) => {
  const { t } = useTranslation();
  const { farm, site } = useFarm();
  const [order, setOrder] = useState<Key[]>([]);
  const trackEvent = makeTrackEvent(GACategories.sharing);

  const {
    meta: { isAdmin },
    api: { isFarmerOf }
  } = useCurrentUser();

  const {
    dataDescriptors,
    api: { getChartInfo, resolveMetricByVariantID },
    meta: { isLoading }
  } = useDataDictionary();

  const {
    api: { removeChart, updateChartOrder }
  } = useSavedCharts();

  const page = useSelector((state: any) => Enterprise.entities.getPage(state, { id: pageID }));
  const { cards, name } = page;

  /**
   * Route to configurator with the chart
   * @param {CardEntity["id"]} chartID
   */
  const handleClick = (chartID: CardEntity["id"]) => {
    if (chartID) {
      Enterprise.history.push({
        state: {
          editChart: {
            chartID,
            dashboard: pageID
          }
        }
      });
    }
  };

  /**
   * Move card in the list
   * @param {React.Key} dragKey
   * @param {React.Key} hoverKey
   */
  const moveNode = (dragKey: Key, hoverKey: Key) => {
    const newOrder = order.slice();

    cards.forEach((item) => {
      if (item.id && newOrder.indexOf(item.id) === -1) {
        newOrder.push(item.id);
      }
    });

    const dragIndex = newOrder.indexOf(dragKey);
    const hoverIndex = newOrder.indexOf(hoverKey);

    newOrder.splice(dragIndex, 1);
    newOrder.splice(hoverIndex, 0, dragKey);

    updateChartOrder(pageID, newOrder);
    setOrder(newOrder);
  };

  /**
   * Delete handler with pop confirm
   * @param {string} chartID
   * @param {string} metricName
   */
  const handleDelete = (chartID: string, metricName: string) => {
    const { id, name } = page;

    /**
     * Opens up a confirmation modal
     */
    confirm({
      ...commonModalProps,
      okText: t(confirmCopy.yes),
      cancelText: t(confirmCopy.no),
      title: (
        <Space>
          <DeleteOutlined />
          {t(removeChartCopy.title)}
        </Space>
      ),
      content: t(removeChartCopy.description),
      onOk() {
        GAEvent(
          GACategories.dashboards,
          "Remove metric",
          JSON.stringify({ metric: metricName, dashboard: name })
        );

        removeChart(chartID, id);
      }
    });
  };

  /**
   * Order the charts based on the provided order state
   * @type {{layers: LayerResponse[], id: string, displayName: Nullable<string>}[]}
   */
  const orderedCards = _.sortBy(cards, (card) => {
    const orderIndex = _.indexOf(order, card.id);

    return orderIndex === -1 ? _.findIndex(cards, ({ id }) => id === card.id) : orderIndex;
  });

  const [refs, setRefs] = useState<Record<string, any>>(
    _.reduce(orderedCards, (acc, { id }) => ({ ...acc, [id]: createRef() }), {})
  );

  useDeepCompareEffect(() => {
    setRefs((prevState) => ({
      ..._.reduce(orderedCards, (acc, { id }) => ({ ...acc, [id]: createRef() }), {}),
      ..._.omit(prevState, _.difference(_.keys(prevState), _.map(orderedCards, "id")))
    }));
  }, [orderedCards]);

  _.forEach(orderedCards, ({ id, variantIDs }) => {
    if (_.isEmpty(variantIDs)) {
      throw new Error(`Card with ID ${id} has no variants.`);
    }
  });

  return (
    <Row gutter={[24, 24]}>
      {!isLoading &&
        !_.isEmpty(dataDescriptors) &&
        _.map(orderedCards, (savedCard) => {
          const {
            id,
            displayName: userDefinedTitle,
            variantIDs,
            dataFilters,
            displayFilters,
            dateRange,
            chartType,
            bucketSize,
            clientParams
          } = savedCard;

          const [main] = variantIDs;

          let metric;

          try {
            metric = resolveMetricByVariantID(main);
          } catch (e) {
            return null;
          }

          const { metricID, variantID } = {
            metricID: metric.id,
            variantID: main
          };

          const chartInfo = getChartInfo(metricID, main);

          /**
           * dateRange can either b iso duration in an array e.g ["P1M"] or a custom range e.g. ["2023-05-01", "2023-06-01"]
           * Supply props to api context based on length of array
           */
          const isoDuration = dateRange && _.size(dateRange) === 1 ? _.first(dateRange) : null;
          const [startDate, endDate] = dateRange && _.size(dateRange) === 2 ? dateRange : [];

          const durationProps = isoDuration
            ? { isoDuration: isoDuration as ISODuration }
            : startDate && endDate && { timePeriod: { startDate, endDate }, isoDuration: null };

          /**
           * Pick the data descriptors for the variants in the card
           * @type {any[]}
           */
          const variants = _.map(variantIDs, (id) => dataDescriptors[id]);

          /**
           * Format the filters to be passed to the filter context
           * @type {Record<DataDescriptorEntity["id"], Record<string, unknown>>}
           */
          const filters = formatFilters(
            _.merge(_.defaultTo(dataFilters, {}), makeDefaultDataFilters(variants, site))
          );

          /**
           * Resolve the series filter from the current card
           * @type {NumericDictionary<unknown>}
           */
          const seriesFilter = _.omitBy(
            _.mapValues(displayFilters, (filters) =>
              _.find(filters, {
                type: "series"
              })
            ),
            _.isUndefined
          );

          /**
           * Get the filter values
           * @type {{[p: number]: any}}
           */
          const seriesValues = _.mapValues(seriesFilter, (filter) => _.get(filter, "values", []));

          const metricDisplayName = chartInfo?.nameToDisplay
            ? t(displayName(chartInfo?.nameToDisplay), { makeDefaultValue: true })
            : "";

          return (
            metricID &&
            variantID && (
              <Col
                /**
                 * Using ID doesn't re-render the component with filter/daterange changes
                 */
                key={JSON.stringify(savedCard)}
                lg={12}
                span={24}
                data-gtm-category={GACategories.configurator}
                data-gtm-action="Go to chart configurator"
                data-gtm-info={JSON.stringify({ metric: metricDisplayName })}
              >
                <ChartFilterContext.Provider
                  series={seriesValues}
                  filters={filters}
                  bucketSize={bucketSize}
                  {...durationProps}
                >
                  <ChartFilterContext.Consumer>
                    {(filterContext) => (
                      <ChartAPIContext.Provider
                        metricVariantID={variantIDs}
                        farmID={farm?.id}
                        defaultView={chartType}
                      >
                        <ChartAPIContext.Consumer>
                          {({
                            charts,
                            charts: [{ entity }],
                            metricVariants,
                            metricVariants: [
                              {
                                dataProperties: { typeName }
                              }
                            ],
                            meta: { requestURL }
                          }) => (
                            <DndProvider backend={HTML5Backend}>
                              <ChartCard
                                onClick={(e) => {
                                  Enterprise.history.push(
                                    CompiledRoutes.app.analytics.compareCharts.withCard({
                                      source: "card",
                                      objectID: id
                                    })
                                  );
                                }}
                                handleReport={
                                  isMutableRef<any>(refs[id])
                                    ? async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();

                                        const base64 = await resizeBase64Image(
                                          refs[id].current.toDataURL("image/png"),
                                          1280
                                        );

                                        Enterprise.history.push({
                                          state: {
                                            feedback: {
                                              sourceID: entity.id,
                                              sourceType: "Chart",
                                              context: {
                                                timePeriod: filterContext.timePeriod,
                                                typeName
                                              },
                                              base64Content: base64
                                            }
                                          }
                                        });
                                      }
                                    : void 0
                                }
                                index={id}
                                className={styles.Card}
                                metric={_.defaultTo(userDefinedTitle, metricDisplayName)}
                                variant={chartInfo?.variantType ? t(variantCopy(chartInfo?.variantType)) : ""}
                                source={
                                  chartInfo?.sourceNames &&
                                  _.join(
                                    _.map(chartInfo?.sourceNames, (sourceName) =>
                                      t<string>(sourceCopy(sourceName))
                                    ),
                                    ", "
                                  )
                                }
                                labels={{
                                  remove: t(cta.save, { isSaved: true }),
                                  edit: t<string>(pageTitle),
                                  report: t<string>(cta.report),
                                  share: t<string>(cta.share)
                                }}
                                handleDrag={moveNode}
                                data-gtm-info={JSON.stringify({
                                  metric: metricDisplayName,
                                  dashboard: name
                                })}
                                handleConfigure={(event) => {
                                  event.stopPropagation();
                                  if (_.toLower(_.get(event, ["target", "tagName"])) !== "canvas") {
                                    handleClick(id);
                                  }
                                }}
                                handleDelete={(e) => {
                                  e.stopPropagation();
                                  handleDelete(id, metricDisplayName);
                                }}
                                handleShare={
                                  (isAdmin || isFarmerOf(farm?.id)) && isMutableRef<any>(refs[id])
                                    ? async (e) => {
                                        trackEvent(GAActions.shareChart, { label: metricDisplayName });

                                        e.preventDefault();
                                        e.stopPropagation();

                                        const base64 = await resizeBase64Image(
                                          refs[id].current.toDataURL("image/png"),
                                          1280
                                        );

                                        const metricVariantIDCollection = _.map(metricVariants, "id");

                                        const seriesDefinitions = _.reduce(
                                          metricVariantIDCollection,
                                          (acc, id) => {
                                            const series = filterContext.api.getSeries(id);

                                            if (series && !_.isEmpty(series)) {
                                              acc[id] = series;
                                            }
                                            return acc;
                                          },
                                          {} as Record<string, SeriesDefinition>
                                        );

                                        const metadata = _.reduce(
                                          metricVariantIDCollection,
                                          (acc, id) => {
                                            const meta = filterContext.api.getMetadata(id);

                                            if (meta && !_.isEmpty(meta)) {
                                              acc[id] = meta;
                                            }
                                            return acc;
                                          },
                                          {} as Record<string, FilterMetadata>
                                        );

                                        Enterprise.history.push({
                                          state: {
                                            shareChart: {
                                              instance: +new Date(),
                                              chartIDCollection: _.map(charts, "entity.id"),
                                              farmID: farm?.id,
                                              requestURL,
                                              base64Content: base64,
                                              filterContext: {
                                                ..._.pick(filterContext, [
                                                  "filters",
                                                  "timePeriod",
                                                  "isoDuration"
                                                ]),
                                                series: seriesDefinitions,
                                                metadata
                                              },
                                              layers: metricVariantIDCollection,
                                              dataDescriptorID: variantID
                                            }
                                          }
                                        });
                                      }
                                    : void 0
                                }
                              >
                                <Col flex={1} style={{ padding: 0 }}>
                                  <Chart
                                    variant={Charts.ChartVariant.Embedded}
                                    ref={refs[id]}
                                    config={{
                                      ...clientParams,
                                      onReady: (plot) => {
                                        if (refs[id]) {
                                          refs[id].current = plot;
                                        } else {
                                          console.warn("Ref not found");
                                        }
                                      }
                                    }}
                                  />
                                </Col>
                              </ChartCard>
                            </DndProvider>
                          )}
                        </ChartAPIContext.Consumer>
                      </ChartAPIContext.Provider>
                    )}
                  </ChartFilterContext.Consumer>
                </ChartFilterContext.Provider>
              </Col>
            )
          );
        })}
      <Col
        lg={12}
        span={24}
        data-gtm-category={GACategories.configurator}
        data-gtm-action="Go to chart selector from empty"
        data-gtm-info={JSON.stringify({
          dashboard: name
        })}
      >
        <ContentWrapper>
          <Empty
            className={styles.Empty}
            variant="charts"
            title={t(empty.charts.title)}
            description={
              <Button
                type="link"
                onClick={() =>
                  Enterprise.history.push({
                    pathname: CompiledRoutes.app.analytics.dashboard.addChart.index({
                      dashboard: pageID
                    })
                  })
                }
              >
                {t(empty.charts.cta)}
              </Button>
            }
          />
        </ContentWrapper>
      </Col>
    </Row>
  );
};
